import React, { useState } from 'react';
import { Play, Pause, X, Rewind, FastForward, Loader2, Volume2 } from 'lucide-react';
import { useAudio } from '../context/AudioContext';
import FullPlayerScreen from './FullPlayerScreen';

const fmt = (s) => {
  if (!isFinite(s) || s < 0) return '0:00';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
};

/**
 * GlobalMiniPlayer — persistent audio bar that floats just above the bottom
 * nav whenever a clip is loaded. Renders nothing when no track is active,
 * so it never eats layout on screens without audio.
 */
const GlobalMiniPlayer = () => {
  const { state, toggle, skip, seek, close } = useAudio();
  const { current, playing, loading, position, duration, error } = state;
  const [fullOpen, setFullOpen] = useState(false);
  if (!current) return null;

  const pct = duration > 0 ? Math.min(100, (position / duration) * 100) : 0;

  return (
    <div
      className="fixed z-40 w-full max-w-[640px] left-1/2 -translate-x-1/2"
      style={{ bottom: 72 }}
      data-testid="global-mini-player"
      role="region"
      aria-label="Audio player"
    >
      <div
        className="mx-2 mb-2 rounded-2xl overflow-hidden shadow-xl"
        style={{
          background: 'linear-gradient(135deg, #173F35 0%, #245549 100%)',
          border: '1px solid rgba(214,173,96,0.35)',
          boxShadow: '0 10px 30px rgba(0,0,0,0.35)',
        }}
      >
        {/* Progress bar */}
        <div
          role="slider"
          aria-label="Seek"
          aria-valuemin={0}
          aria-valuemax={duration || 0}
          aria-valuenow={position}
          className="relative h-1 bg-black/30 cursor-pointer"
          onClick={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            const ratio = (e.clientX - rect.left) / rect.width;
            seek(ratio * (duration || 0));
          }}
        >
          <div
            className="absolute inset-y-0 left-0 transition-[width] duration-200"
            style={{ width: `${pct}%`, background: '#D6AD60' }}
          />
        </div>

        {/* Body */}
        <div className="flex items-center gap-3 px-3 py-2.5">
          {/* Artwork + title area — tap to open full player */}
          <button
            type="button"
            onClick={() => setFullOpen(true)}
            className="flex items-center gap-3 flex-1 min-w-0 text-left"
            data-testid="mini-player-open-full"
            aria-label="Open full player"
          >
            <div
              className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden"
              style={{ background: 'rgba(214,173,96,0.22)' }}
            >
              {current.artwork ? (
                <img src={current.artwork} alt="" className="w-full h-full object-cover" />
              ) : (
                <Volume2 className="w-5 h-5" style={{ color: '#D6AD60' }} />
              )}
            </div>

            {/* Title + timing */}
            <div className="flex-1 min-w-0">
              <p
                className="text-[13px] font-semibold truncate"
                style={{ color: '#F8F6F0', fontFamily: "'Playfair Display', serif" }}
                data-testid="mini-player-title"
              >
                {current.title || 'Now playing'}
              </p>
              <p className="text-[11px] truncate" style={{ color: 'rgba(248,246,240,0.7)' }}>
                {error ? error : (current.subtitle || `${fmt(position)} / ${fmt(duration)}`)}
              </p>
            </div>
          </button>

          {/* Controls */}
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => skip(-15)}
              className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-white/10 transition-colors"
              data-testid="mini-player-back-15"
              aria-label="Back 15 seconds"
            >
              <Rewind className="w-4 h-4" style={{ color: '#F8F6F0' }} />
            </button>
            <button
              type="button"
              onClick={toggle}
              className="w-11 h-11 rounded-full flex items-center justify-center transition-transform active:scale-95"
              style={{ background: '#D6AD60' }}
              data-testid="mini-player-toggle"
              aria-label={playing ? 'Pause' : 'Play'}
              aria-pressed={playing}
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" style={{ color: '#1a1814' }} />
              ) : playing ? (
                <Pause className="w-5 h-5" style={{ color: '#1a1814' }} />
              ) : (
                <Play className="w-5 h-5 ml-0.5" style={{ color: '#1a1814' }} />
              )}
            </button>
            <button
              type="button"
              onClick={() => skip(15)}
              className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-white/10 transition-colors"
              data-testid="mini-player-forward-15"
              aria-label="Forward 15 seconds"
            >
              <FastForward className="w-4 h-4" style={{ color: '#F8F6F0' }} />
            </button>
            <button
              type="button"
              onClick={close}
              className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-white/10 transition-colors ml-0.5"
              data-testid="mini-player-close"
              aria-label="Close player"
            >
              <X className="w-4 h-4" style={{ color: 'rgba(248,246,240,0.75)' }} />
            </button>
          </div>
        </div>
      </div>
      <FullPlayerScreen open={fullOpen} onClose={() => setFullOpen(false)} />
    </div>
  );
};

export default GlobalMiniPlayer;
